import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import './HowItWorksModal.css';

const HowItWorksModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="how-it-works-modal"
          initial={{ opacity: 0, scale: 0.9, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 50 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>📦 How it works</h2>
            <button
              className="close-button"
              onClick={onClose}
              aria-label="Close modal"
            >
              <X size={24} />
            </button>
          </div>

          <div className="modal-content">
            <div className="intro-section">
              <p>
                We are human. We make mistakes. Sometimes we do bad things, and sometimes we do harm by doing nothing at all. We hurt others. We have thoughts that make us feel guilty just for thinking them.
              </p>
              <p>
                These mistakes sometimes have lasting effects, sometimes they are just momentary. They have one thing in common: <strong>You cannot erase them.</strong>
              </p>
              <p className="highlight">
                <strong>But you can counterbalance them!</strong>
              </p>
            </div>

            <div className="platform-section">
              <p>
                The GuiltBox platform gives you the opportunity to free yourself from your mistakes. A chance that even if something has already happened, good can come from it.
              </p>
              <p className="call-to-action">
                <strong>Just tell your story, be honest!</strong> The community values this. By doing so, you can provide donations to organizations that work globally for a better world.
              </p>
            </div>

            <div className="how-it-works-steps-section">
              <h3>What do you need to do?</h3>
              <div className="how-it-works-steps">
                <div className="how-it-works-step">
                  <div className="how-it-works-step-number">1</div>
                  <div className="how-it-works-step-content">
                    <h4>Write your confession</h4>
                    <p>Describe the essence of your confession in 1-2 sentences.</p>
                  </div>
                </div>
                
                <div className="how-it-works-step">
                  <div className="how-it-works-step-number">2</div>
                  <div className="how-it-works-step-content">
                    <h4>Tell your story</h4>
                    <p>Share it in your own words. Spoken words have power. What happened? Where? When? What were you thinking and how did you feel afterwards?</p>
                  </div>
                </div>
                
                <div className="how-it-works-step">
                  <div className="how-it-works-step-number">3</div>
                  <div className="how-it-works-step-content">
                    <h4>Choose 3 causes</h4>
                    <p>Select 3 causes close to your heart. Donations from your story will be distributed equally among the 3 organizations and the GuiltBox platform (25% each), ensuring long-term sustainability.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="token-section">
              <h3>🪙 Your Story Becomes a Token</h3>
              <p>
                An ERC20 token is created from your story on the Zora platform. Anyone who supports you and the organizations by purchasing your token helps provide donations to the causes you selected.
              </p>
              <p className="transformation">
                <strong>This is how a mistake, a story, becomes an opportunity to help others!</strong>
              </p>
            </div>

            <div className="privacy-section">
              <h3>🔒 Complete Anonymity</h3>
              <p>
                <strong>Important:</strong> Using GuiltBox is completely anonymous! No registration required. We don't ask for names or email addresses. On GuiltBox, there's only you, your honesty, and the community that values it. They support you and the causes you choose.
              </p>
            </div>

            <div className="footer-section">
              <p className="tagline">
                💝 9 Charities Supported | 📖 ∞ Stories Shared | 🔍 100% Transparent
              </p>
              <p className="made-with">Made with ❤️ for a better world</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default HowItWorksModal;